import React, { useState, useMemo, useEffect } from "react";
import { motion } from 'framer-motion';
import { Image, Search, X, ArrowRight, ExternalLink, Play, MessageCircle, Sparkles } from 'lucide-react';

const Gallery = () => {
  const [selectedImage, setSelectedImage] = useState(null);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const images = [
    {
      src: './images/consulta_ruc.png',
      alt: 'Consulta RUC en Tiempo Real',
      category: 'Dashboard',
      description: 'Interfaz moderna para consultas masivas de RUC/DNI',
      features: ['Búsqueda en tiempo real', 'Exportación CSV', 'Validación SUNAT']
    },
    {
      src: './images/envio_comprobantes.png',
      alt: 'Envío Masivo Facturas',
      category: 'Sistema ERP',
      description: 'Plataforma de emisión masiva con tracking en tiempo real',
      features: ['Emisión por lotes', 'Tracking CDR', 'Reintentos automáticos']
    },
    {
      src: './images/gestionapis.png',
      alt: 'Gestor Documental',
      category: 'Documentación',
      description: 'Sistema de gestión y descarga de archivos SUNAT',
      features: ['Multi-formato', 'Compresión ZIP', 'Búsqueda avanzada']
    },
    {
      src: './images/buzon_sol.png',
      alt: 'Buzón Electrónico SUNAT',
      category: 'Comunicaciones',
      description: 'Monitoreo automatizado de comunicaciones oficiales',
      features: ['Webhooks', 'Filtros inteligentes', 'Alertas push']
    },
    {
      src: './images/anular_factura.png',
      alt: 'Gestor de Anulaciones',
      category: 'Notas',
      description: 'Plataforma especializada en notas de crédito/débito',
      features: ['Anulaciones seguras', 'Historial completo', 'CDR inmediato']
    },
    {
      src: './images/tipocambio.png',
      alt: 'Sistema Tipo Cambio',
      category: 'Finanzas',
      description: 'Dashboard de tasas de cambio con histórico completo',
      features: ['Actualización diaria', 'Múltiples monedas', 'Gráficos históricos']
    }
  ];

  // Función para abrir WhatsApp con el mensaje personalizado
  const openWhatsAppDemo = (imageData = null) => {
    let message = "Hola, estoy interesado en solicitar una demo personalizada.";
    
    if (imageData) {
      message += ` Me interesa especialmente la funcionalidad: ${imageData.alt} - ${imageData.description}`;
    }
    
    // Codificar el mensaje para URL
    const encodedMessage = encodeURIComponent(message);
    const phoneNumber = "51955000321";
    
    // Abrir WhatsApp
    window.open(`https://wa.me/${phoneNumber}?text=${encodedMessage}`, '_blank');
  };

  return (
    <section className="py-20 bg-gradient-to-br from-slate-50 via-white to-cyan-50 relative overflow-hidden border-t border-gray-200">
      {/* Efectos de fondo consistentes */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute inset-0 opacity-[0.02]">
          <div className="absolute inset-0 bg-[linear-gradient(#000_1px,transparent_1px),linear-gradient(90deg,#000_1px,transparent_1px)] bg-[size:64px_64px]" />
        </div>

        {[...Array(8)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-1 h-1 bg-cyan-500/20 rounded-full"
            initial={{
              x: Math.random() * window.innerWidth,
              y: Math.random() * window.innerHeight / 2 + 100,
            }}
            animate={{
              scale: [1, 1.8, 1],
            }}
            transition={{
              duration: 4 + Math.random() * 3,
              repeat: Infinity,
              delay: Math.random() * 4,
            }}
          />
        ))}
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl relative z-10">
        {/* Encabezado */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true, margin: "-50px" }}
          className="text-center mb-16"
        >
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            viewport={{ once: true }}
            className="inline-flex items-center space-x-2 bg-cyan-500/10 border border-cyan-500/20 rounded-lg px-4 py-2 mb-6"
          >
            <Image className="w-4 h-4 text-cyan-600" />
            <span className="text-sm font-mono text-cyan-700">UI EXAMPLES • DASHBOARDS • INTEGRATIONS</span>
          </motion.div>

          <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4 leading-tight">
            Galería de{' '}
            <span className="bg-gradient-to-r from-cyan-600 to-blue-600 bg-clip-text text-transparent">
              Integraciones
            </span>
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed font-light">
            Ejemplos reales de implementación en dashboards empresariales, sistemas ERP
            y aplicaciones web especializadas para SUNAT.
          </p>
        </motion.div>

        {/* Grid de imágenes */}
        <motion.div
          className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.8, staggerChildren: 0.1 }}
          viewport={{ once: true }}
        >
          {images.map((image, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1, duration: 0.5 }}
              viewport={{ once: true }}
              className="group bg-white border border-gray-200 rounded-2xl overflow-hidden hover:border-cyan-300 hover:shadow-xl transition-all duration-300"
              whileHover={{ y: -4 }}
            >
              {/* Imagen con overlay */}
              <div
                className="relative aspect-video bg-gray-100 cursor-pointer overflow-hidden"
                onClick={() => setSelectedImage(image)}
              >
                <img
                  src={image.src}
                  alt={image.alt}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />

                {/* Overlay hover */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-all duration-300 flex flex-col justify-between p-6">
                  <div className="flex justify-between items-start">
                    <span className="bg-cyan-500 text-white text-xs font-medium px-2 py-1 rounded">
                      {image.category}
                    </span>
                    <motion.div
                      whileHover={{ scale: 1.1 }}
                      className="bg-white/20 backdrop-blur-sm rounded-full p-2"
                    >
                      <Search className="w-4 h-4 text-white" />
                    </motion.div>
                  </div>

                  <div className="text-white">
                    <h3 className="font-bold text-lg mb-2">{image.alt}</h3>
                    <p className="text-sm text-white/90 mb-3">{image.description}</p>

                    <div className="flex flex-wrap gap-1 mb-3">
                      {image.features.map((feature, featureIndex) => (
                        <span
                          key={featureIndex}
                          className="bg-white/20 text-white text-xs px-2 py-1 rounded"
                        >
                          {feature}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Footer de la tarjeta */}
              <div className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold text-gray-900 text-sm">{image.alt}</h3>
                    <p className="text-gray-500 text-xs">{image.category}</p>
                  </div>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="text-cyan-600 hover:text-cyan-700 transition-colors"
                    onClick={() => setSelectedImage(image)}
                  >
                    <ExternalLink className="w-4 h-4" />
                  </motion.button>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          viewport={{ once: true }}
          className="mt-16 text-center"
        >
          <div className="bg-white border border-gray-200 rounded-2xl p-8 max-w-2xl mx-auto relative overflow-hidden">
            {/* Efectos de fondo para el CTA */}
            <div className="absolute inset-0 overflow-hidden">
              <div className="absolute -top-10 -right-10 w-20 h-20 bg-green-500/5 rounded-full blur-xl"></div>
              <div className="absolute -bottom-10 -left-10 w-20 h-20 bg-cyan-500/5 rounded-full blur-xl"></div>
            </div>
            
            <h3 className="text-xl font-bold text-gray-900 mb-2 relative z-10">¿Quieres ver más implementaciones?</h3>
            <p className="text-gray-600 mb-6 relative z-10">Tenemos casos de estudio detallados y demos en vivo disponibles</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center relative z-10">
              {/* Botón principal mejorado */}
              <motion.button
                whileHover={{ 
                  scale: 1.05,
                  y: -2
                }}
                whileTap={{ scale: 0.95 }}
                onClick={() => openWhatsAppDemo()}
                className="relative bg-gradient-to-r from-green-500 to-green-600 text-white px-8 py-4 rounded-xl font-semibold text-sm border-0 hover:shadow-2xl transition-all duration-300 inline-flex items-center space-x-3 group overflow-hidden"
                style={{
                  boxShadow: '0 10px 30px -5px rgba(34, 197, 94, 0.3)'
                }}
              >
                {/* Efecto de brillo al hover */}
                <div className="absolute inset-0 bg-gradient-to-r from-green-400 to-green-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                
                {/* Partículas animadas */}
                <motion.div
                  className="absolute inset-0 opacity-0 group-hover:opacity-100"
                  initial={false}
                >
                  {[...Array(3)].map((_, i) => (
                    <motion.div
                      key={i}
                      className="absolute w-2 h-2 bg-white/30 rounded-full"
                      initial={{ x: -10, y: Math.random() * 40 }}
                      animate={{ 
                        x: 120,
                        y: Math.random() * 40
                      }}
                      transition={{
                        duration: 1.5,
                        repeat: Infinity,
                        delay: i * 0.5,
                        ease: "easeOut"
                      }}
                    />
                  ))}
                </motion.div>

                {/* Icono con animación */}
                <motion.div
                  className="relative z-10"
                  whileHover={{ 
                    scale: 1.1,
                    rotate: [0, -5, 5, 0]
                  }}
                  transition={{ duration: 0.3 }}
                >
                  <MessageCircle className="w-5 h-5" />
                </motion.div>
                
                <span className="relative z-10">Solicitar Demo por WhatsApp</span>
                
                {/* Icono de sparkle */}
                <motion.div
                  className="relative z-10"
                  animate={{ 
                    scale: [1, 1.2, 1],
                    rotate: [0, 10, 0]
                  }}
                  transition={{ 
                    duration: 2,
                    repeat: Infinity,
                    repeatType: "reverse"
                  }}
                >
                  <Sparkles className="w-4 h-4" />
                </motion.div>

                {/* Efecto de borde luminoso */}
                <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-green-400 to-cyan-400 opacity-0 group-hover:opacity-100 transition-opacity duration-300 -z-10 blur-sm group-hover:blur-md"></div>
              </motion.button>

              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="border border-gray-300 text-gray-700 bg-white px-6 py-3 rounded-lg font-medium text-sm hover:border-gray-400 transition-colors inline-flex items-center space-x-2"
              >
                <span>Casos de Estudio</span>
                <ArrowRight className="w-4 h-4" />
              </motion.button>
            </div>
          </div>
        </motion.div>

        {/* Modal de imagen */}
        {selectedImage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 flex items-center justify-center z-50 p-4 bg-black/80 backdrop-blur-sm"
            onClick={() => setSelectedImage(null)}
          >
            <motion.div
              className="relative max-w-6xl w-full max-h-[90vh] mx-auto bg-white rounded-2xl overflow-hidden"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex flex-col lg:flex-row">
                {/* Imagen */}
                <div className="lg:w-2/3 bg-gray-100">
                  <img
                    src={selectedImage.src}
                    alt={selectedImage.alt}
                    className="w-full h-auto max-h-[70vh] object-contain"
                  />
                </div>

                {/* Información */}
                <div className="lg:w-1/3 p-6">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <span className="bg-cyan-100 text-cyan-700 text-xs font-medium px-2 py-1 rounded">
                        {selectedImage.category}
                      </span>
                      <h3 className="text-xl font-bold text-gray-900 mt-2">{selectedImage.alt}</h3>
                    </div>
                    <motion.button
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      className="bg-gray-100 rounded-full p-2 hover:bg-gray-200 transition-colors"
                      onClick={() => setSelectedImage(null)}
                    >
                      <X className="w-5 h-5 text-gray-700" />
                    </motion.button>
                  </div>

                  <p className="text-gray-600 mb-4">{selectedImage.description}</p>

                  <div className="space-y-2 mb-6">
                    <h4 className="font-semibold text-gray-900 text-sm">Características:</h4>
                    {selectedImage.features.map((feature, index) => (
                      <div key={index} className="flex items-center space-x-2">
                        <div className="w-1.5 h-1.5 bg-cyan-500 rounded-full"></div>
                        <span className="text-gray-600 text-sm">{feature}</span>
                      </div>
                    ))}
                  </div>

                  {/* Botón mejorado en el modal */}
                  <motion.button
                    whileHover={{ 
                      scale: 1.02,
                      y: -1
                    }}
                    whileTap={{ scale: 0.98 }}
                    className="relative w-full bg-gradient-to-r from-green-500 to-green-600 text-white py-4 rounded-xl font-semibold text-sm border-0 hover:shadow-xl transition-all duration-300 group overflow-hidden"
                    onClick={() => {
                      openWhatsAppDemo(selectedImage);
                      setSelectedImage(null);
                    }}
                    style={{
                      boxShadow: '0 8px 25px -5px rgba(34, 197, 94, 0.3)'
                    }}
                  >
                    {/* Efecto de fondo animado */}
                    <div className="absolute inset-0 bg-gradient-to-r from-green-400 to-green-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                    
                    {/* Partículas */}
                    <motion.div
                      className="absolute inset-0 opacity-0 group-hover:opacity-100"
                      initial={false}
                    >
                      {[...Array(2)].map((_, i) => (
                        <motion.div
                          key={i}
                          className="absolute w-1.5 h-1.5 bg-white/40 rounded-full"
                          initial={{ x: -5, y: Math.random() * 50 }}
                          animate={{ 
                            x: 320,
                            y: Math.random() * 50
                          }}
                          transition={{
                            duration: 1.2,
                            repeat: Infinity,
                            delay: i * 0.6,
                            ease: "easeOut"
                          }}
                        />
                      ))}
                    </motion.div>

                    <div className="relative z-10 flex items-center justify-center space-x-2">
                      <MessageCircle className="w-5 h-5" />
                      <span>Solicitar Demo Personalizada</span>
                      <motion.div
                        animate={{ 
                          scale: [1, 1.2, 1],
                        }}
                        transition={{ 
                          duration: 1.5,
                          repeat: Infinity
                        }}
                      >
                        <Sparkles className="w-4 h-4" />
                      </motion.div>
                    </div>

                    {/* Efecto de borde luminoso */}
                    <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-green-400 to-cyan-400 opacity-0 group-hover:opacity-100 transition-opacity duration-300 -z-10 blur group-hover:blur-md"></div>
                  </motion.button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </div>
    </section>
  );
};

export default Gallery;